import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { fabric } from 'fabric';
import { ToastrService } from 'ngx-toastr';

import { AdminUrlBuilder } from '../../../../../shared/admin/api/admin-url-builder';
import { PrintTemplateAssetUploader } from '../../../../../shared/print-template/print-template-asset-uploader';
import { PrintTemplateDefaults } from '../../../../../shared/print-template/print-template-defaults';
import {
  aspectRatioValue,
  cropInfo,
  DEFAULT_PHOTO_ORIENTATION,
  IPrintTemplateLayouts,
  layoutCombinations,
  PRIMARY_LAYOUT_ORIENTATION,
  PRIMARY_LAYOUT_RATIO,
  PHOTO_ORIENTATIONS,
  IPrintTemplate,
  IPrintTemplatePosition,
  IPrintTemplatePositions,
  lockedHeight,
  PhotoOrientation,
  PositionAspectRatio,
  ROTATION_OPTIONS,
  sheetSizeMm,
  TemplateAspectRatio,
} from '../../../../../shared/print-template/print-template.model';

const SNAP_THRESHOLD_PX = 6;
/** Nothing of the slot lands on the sheet, so it would print nothing at all. */
const fullyOutside = (position: IPrintTemplatePosition): boolean =>
  position.x + position.width <= 0 || position.y + position.height <= 0 ||
  position.x >= 1 || position.y >= 1;
/** Part of the slot hangs over an edge — allowed, but worth saying out loud. */
const partlyOutside = (position: IPrintTemplatePosition): boolean =>
  position.x < 0 || position.y < 0 ||
  position.x + position.width > 1 || position.y + position.height > 1;
const NUDGE_MM = 0.1;
const NUDGE_MM_LARGE = 1;

@Component({
  selector: 'admin-print-template-editor',
  templateUrl: './editor.component.html',
  styleUrls: [ './editor.component.scss' ],
})
export class AdminPrintTemplateEditorComponent implements OnInit, OnChanges, OnDestroy {

  @Input() row: any;
  @Output() makeDefault = new EventEmitter<any>();
  @Output() created = new EventEmitter<any>();
  @Output() discarded = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<any>();

  @ViewChild('fileUploader', { static: false }) fileUploader: any;
  @ViewChild('editorContainer', { static: false }) editorContainer: ElementRef;
  @ViewChild('editor', { static: false }) editor: ElementRef;

  isEditMode: boolean = false;
  loading: boolean = false;
  saving: boolean = false;
  previewing: boolean = false;

  template: IPrintTemplate;
  /**
   * Slots per photo ratio and orientation. The printed design is one, but how much of the photo fits
   * through its windows is not, so each combination carries its own layout.
   */
  layouts: IPrintTemplateLayouts = {};
  activeRatio: PositionAspectRatio = PRIMARY_LAYOUT_RATIO;
  activeOrientation: PhotoOrientation = PRIMARY_LAYOUT_ORIENTATION;
  activeSlot: number = 0;

  /** Ratio first, orientation second — the order staff walks them and "use previous" follows. */
  get combinations(): { ratio: PositionAspectRatio; orientation: PhotoOrientation }[] {
    return layoutCombinations();
  }

  get ratios(): PositionAspectRatio[] {
    return [ PRIMARY_LAYOUT_RATIO, '4x3', '1x1' ];
  }

  /** The first combination is laid out by hand; every other one can start from the one before it. */
  get isPrimaryCombination(): boolean {
    return this.activeRatio === PRIMARY_LAYOUT_RATIO
      && this.activeOrientation === PRIMARY_LAYOUT_ORIENTATION;
  }

  private get variants(): { [orientation: string]: IPrintTemplatePosition[] } {
    const layout = this.layouts[ this.activeRatio ];

    return layout ? <any>layout : { horizontal: [], vertical: [] };
  }

  /** True while the editor shows the built-in starting layout, nothing having been saved yet. */
  usingDefaults: boolean = false;

  /**
   * Whether the template came back positioned. An empty main layout still counts: a positioned
   * template is allowed to have no slots, and that is a different thing from a legacy one that has
   * never been positioned at all and still prints through the old renderer.
   */
  private hasStoredLayout: boolean = false;

  /**
   * The layouts as the API last confirmed them. The print preview renders what is stored, not what
   * is on screen, so it has to stay out of reach while the two differ — otherwise staff drags a slot,
   * prints, and sees the old sheet.
   */
  private savedSnapshot: string = '';

  get hasUnsavedChanges(): boolean {
    return this.snapshot() !== this.savedSnapshot;
  }

  get canPreview(): boolean {
    return !this.isDraft && !this.usingDefaults && !this.hasUnsavedChanges;
  }

  /** Which combinations are tied counts as part of the layout: untying one changes what is stored. */
  private snapshot(): string {
    return JSON.stringify({ layouts: this.layouts, linked: this.linkedToPrimary });
  }


  get positions(): IPrintTemplatePosition[] {
    return this.variants[ this.activeOrientation ] || [];
  }

  selectRatio(ratio: PositionAspectRatio): void {
    if (ratio === this.activeRatio) {
      return;
    }
    this.activeRatio = ratio;
    this.activeSlot = 0;
    this.syncUnlockedRatio();
    this.buildDraftPhotos();
    this.renderSlots();
  }

  /**
   * Which combinations follow the main one. Everything starts tied to it: a template is laid out
   * once, and the other five are confirmed rather than dragged into place.
   */
  linkedToPrimary: { [key: string]: boolean } = {};

  get isLinked(): boolean {
    return !this.isPrimaryCombination && this.linkedToPrimary[ this.combinationKey ] !== false;
  }

  private isLinkedCombination(ratio: PositionAspectRatio, orientation: PhotoOrientation): boolean {
    if (ratio === PRIMARY_LAYOUT_RATIO && orientation === PRIMARY_LAYOUT_ORIENTATION) {
      return false;
    }

    return this.linkedToPrimary[ ratio + '|' + orientation ] !== false;
  }

  private get combinationKey(): string {
    return this.activeRatio + '|' + this.activeOrientation;
  }

  /**
   * Tying a combination back to the main one throws its own windows away — that is what the tick
   * means. Untying leaves the copy in place as a starting point.
   */
  toggleLinkedToPrimary(): void {
    this.linkedToPrimary[ this.combinationKey ] = !this.isLinked;
    if (this.isLinked) {
      this.copyPrimaryInto(this.activeRatio, this.activeOrientation);
    }
    this.activeSlot = 0;
    this.syncUnlockedRatio();
    this.renderSlots();
  }

  private copyPrimaryInto(ratio: PositionAspectRatio, orientation: PhotoOrientation): void {
    const primary = (this.layouts[ PRIMARY_LAYOUT_RATIO ] || <any>{})[ PRIMARY_LAYOUT_ORIENTATION ] || [];

    this.layouts[ ratio ][ orientation ] = primary
      .map((position: IPrintTemplatePosition) => ({ ...position }));
  }

  /** Keeps every tied combination in step while the main one is being dragged around. */
  private syncLinkedCombinations(): void {
    this.combinations.forEach(({ ratio, orientation }: any) => {
      if (ratio === PRIMARY_LAYOUT_RATIO && orientation === PRIMARY_LAYOUT_ORIENTATION) {
        return;
      }
      if (this.isLinkedCombination(ratio, orientation)) {
        this.copyPrimaryInto(ratio, orientation);
      }
    });
  }


  /** Slots may keep the photo's proportions, or be free when the product window is a fixed size. */
  unlockedRatio: boolean = false;
  rotations = ROTATION_OPTIONS;
  /** Pixels by default — that is what the stored fractions map onto and how the renderer thinks. */
  units: 'mm' | 'inch' | 'px' = 'px';

  get orientations(): { value: PhotoOrientation; label: string }[] {
    return PHOTO_ORIENTATIONS;
  }

  /** Holds the uploaded file id for the drag & drop uploader. */
  uploadForm: FormGroup;

  /** What the template is called. Set when it is created, and renameable afterwards. */
  get templateName(): string {
    return this.template ? this.template.name : (this.row ? this.row.name : '');
  }

  setTemplateName(name: string): void {
    if (this.template) {
      this.template.name = name;
    }
    this.row.name = name;
  }

  /**
   * Photos drawn into every slot so staff sees the real thing, not an empty rectangle. Guests shoot
   * both ways round, so the editor keeps a set and switches between them.
   */
  testPhotos: { url: string; label: string }[] = [];
  activeTestPhoto: number = -1;
  private testPhotoElement: HTMLImageElement;
  private photoObjects: any[] = [];

  /**
   * Background pixel size. Coordinates are normalised against it, so any upload size works —
   * unlike the legacy keyring, which needed an exact 1800x1200 template.
   */
  backgroundWidth: number = 0;
  backgroundHeight: number = 0;

  private canvas: any;
  private slotObjects: any[] = [];
  private labelObjects: any[] = [];
  /** Two red bands per slot showing the strips of photo that cover-cropping throws away. */
  private cropObjects: any[][] = [];
  private canvasWidth: number = 0;
  private canvasHeight: number = 0;
  private syncing: boolean = false;

  constructor(private httpClient: HttpClient,
              private toastrService: ToastrService,
              private urlBuilder: AdminUrlBuilder,
              private assetUploader: PrintTemplateAssetUploader,
              private defaults: PrintTemplateDefaults,
              formBuilder: FormBuilder) {
    this.uploadForm = formBuilder.group({ file: [ '' ] });
  }

  /** A draft has nothing to collapse into, so it opens straight into the editor. */
  ngOnInit(): void {
    this.openDraft();
  }

  /**
   * The list has no trackBy, so inserting a draft at the top hands an existing component a different
   * row instead of building a new one — and its editor would otherwise still hold the old template.
   */
  ngOnChanges(): void {
    if (this.template && this.template.id !== (this.row ? this.row.id : 0)) {
      this.resetForNewRow();
    }
    this.openDraft();
  }

  private openDraft(): void {
    if (this.isDraft && !this.template) {
      this.isEditMode = true;
      this.loadDraft();
    }
  }

  /** Drops everything tied to the row that was here before, so nothing bleeds between templates. */
  private resetForNewRow(): void {
    if (this.canvas) {
      this.canvas.dispose();
      this.canvas = null;
    }
    this.template = null;
    this.layouts = {};
    this.testPhotos = [];
    this.activeTestPhoto = -1;
    this.testPhotoElement = null;
    this.photoObjects = [];
    this.slotObjects = [];
    this.labelObjects = [];
    this.cropObjects = [];
    this.canvasWidth = 0;
    this.canvasHeight = 0;
    this.isEditMode = false;
  }

  /** A row that exists only in the browser: nothing about it has reached the API yet. */
  get isDraft(): boolean {
    return !!this.row && !!this.row.isDraft;
  }

  /** The panel body, canvas included, is only built once staff actually opens the row. */
  editRow(): void {
    this.isEditMode = !this.isEditMode;
    if (this.isEditMode && !this.template) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    if (this.canvas) {
      this.canvas.dispose();
    }
  }

  // -- loading -------------------------------------------------------------------------------

  private load(): void {
    if (this.isDraft) {
      this.loadDraft();
      return;
    }

    this.loading = true;

    this.httpClient.get<any>(this.urlBuilder.buildUrl('/print-template') + '/' + this.row.id).subscribe(
      (template: IPrintTemplate) => {
        this.template = template;
        this.readPositions(template);
        this.loading = false;
        this.loadTestPhoto();
        // Wait for *ngIf="template" to put the canvas element in the DOM.
        setTimeout(() => this.drawBackground());
      },
      (error: any) => {
        this.loading = false;
        this.toastrService.error(this.errorMessage(error));
      }
    );
  }

  /**
   * A draft is assembled from what ships with the app: the product's artwork, an example photo per
   * orientation, and the starting layout. None of it has been uploaded — that happens on save.
   */
  loadDraft(): void {
    this.loading = true;
    this.defaults.forType(this.defaults.adminUrl(), this.row.type).then(
      (positions: IPrintTemplatePosition[]) => {
        this.loading = false;
        this.buildDraft(positions);
      },
      (error: any) => {
        this.loading = false;
        this.toastrService.error(this.errorMessage(error));
        this.discarded.emit();
      }
    );
  }

  private buildDraft(positions: IPrintTemplatePosition[]): void {
    this.template = {
      id: 0,
      name: this.row.name,
      type: this.row.type,
      isDefault: false,
      isActive: true,
      defaultTemplate: false,
      campaign: this.row.campaign,
      file: { id: 0, originalFileName: 'Standard artwork', s3: this.assetUploader.artworkUrl(this.row.type) },
      positions: null,
    };

    this.readPositions(this.template, positions);
    this.buildDraftPhotos();
    setTimeout(() => this.drawBackground());
  }

  get usesDefaultTemplate(): boolean {
    return !!(this.template && this.template.defaultTemplate);
  }

  /** What the editor and the thumbnail show: the shipped artwork, or the uploaded graphic. */
  get backgroundUrl(): string {
    if (this.usesDefaultTemplate) {
      return this.assetUploader.artworkUrl(this.template.type);
    }

    return this.template && this.template.file ? this.template.file.s3 : '';
  }

  /**
   * Switches the template between its own artwork and the one the renderer ships for the type. The
   * flag reaches the API with the next save, the way the name does, so the canvas is redrawn here to
   * show what that save will print.
   */
  setDefaultTemplate(uses: boolean): void {
    if (!this.template) {
      return;
    }

    this.template.defaultTemplate = uses;
    this.resetCanvas();
  }

  /**
   * Draws a stand-in photo per orientation in the ratio being laid out. Redrawn whenever that ratio
   * changes, so the tab and what the slots show never disagree.
   */
  private buildDraftPhotos(): void {
    const ratio: any = this.activeRatio;
    this.testPhotos = [];
    this.activeTestPhoto = -1;
    this.testPhotoElement = null;

    ([ 'horizontal', 'vertical' ] as PhotoOrientation[]).forEach((orientation: PhotoOrientation) => {
      this.addTestPhoto(
        this.assetUploader.examplePhotoUrl(ratio, orientation),
        orientation === this.activeOrientation
      );
    });
  }

  /**
   * Whatever the API returns is what gets edited. A template with no layout at all comes back with
   * `positions: null` — legacy keyrings print through the old renderer that way — so the editor
   * starts from the layout the product has always printed rather than an empty canvas. Nothing is
   * stored until staff saves.
   */
  /**
   * `starting` is only used by a draft: a saved template always carries its own 3x2 horizontal set,
   * and a legacy one with no layout at all is left empty rather than invented.
   */
  private readPositions(template: IPrintTemplate, starting?: IPrintTemplatePosition[]): void {
    const stored = template.positions || <IPrintTemplateLayouts>{};
    const layouts: IPrintTemplateLayouts = {};

    // `null` on a combination means it prints through 3x2 horizontal. A template with nothing stored
    // at all starts the same way: one layout to drag, five to confirm.
    // An array is a layout even when it is empty, so the length is not what decides this.
    const primaryStored = (stored[ PRIMARY_LAYOUT_RATIO ] || <any>{})[ PRIMARY_LAYOUT_ORIENTATION ];
    const anyStored = Array.isArray(primaryStored);
    const primary = anyStored ? primaryStored : (starting || []);

    this.hasStoredLayout = anyStored;

    this.linkedToPrimary = {};
    this.combinations.forEach(({ ratio, orientation }: any) => {
      // `null` is the tie to the main layout. An empty array is not: a combination can legitimately
      // own no slots, and reading that as a tie would show the main slots in its place and save it
      // back as `null`, quietly changing what the template prints.
      const own = (stored[ ratio ] || <any>{})[ orientation ];
      const linked = own === null || typeof own === 'undefined';
      const source = linked ? primary : own;

      layouts[ ratio ] = layouts[ ratio ] || <IPrintTemplatePositions>{ horizontal: [], vertical: [] };
      layouts[ ratio ][ orientation ] = source.map((position: IPrintTemplatePosition) => ({ ...position }));
      this.linkedToPrimary[ ratio + '|' + orientation ] = linked;
    });

    this.layouts = layouts;
    this.usingDefaults = !anyStored;

    this.savedSnapshot = this.snapshot();
    this.activeRatio = PRIMARY_LAYOUT_RATIO;
    this.activeOrientation = PRIMARY_LAYOUT_ORIENTATION;
    this.activeSlot = 0;
  }

  private initCanvas(): void {
    if (this.canvas || !this.editor) {
      return;
    }
    this.canvas = new fabric.Canvas(this.editor.nativeElement, { preserveObjectStacking: true });
    this.canvas.selection = false;
    this.canvas.on({
      'object:modified': () => this.captureFromCanvas(),
      'object:moving': (event: any) => {
        this.snap(event.target);
        this.syncOverlays();
      },
      'object:scaling': () => this.syncOverlays(),
      'selection:created': (event: any) => this.selectFromCanvas(event),
      'selection:updated': (event: any) => this.selectFromCanvas(event),
    });
  }

  private drawBackground(): void {
    // On the GoPhoto design the API hands back no file at all, so the editor draws the panel's own
    // copy of the artwork the renderer prints on.
    const stored = this.template && this.template.file ? this.template.file.s3 || this.template.file.s3File : null;
    const source = this.backgroundUrl || stored;
    if (!source) {
      this.toastrService.error('This template has no background image yet');
      return;
    }

    this.initCanvas();
    if (!this.canvas) {
      return;
    }

    this.loading = true;
    this.loadImage(source).then(
      (element: HTMLImageElement) => this.paintBackground(element),
      () => {
        this.loading = false;
        this.toastrService.error('The background image could not be loaded');
      }
    );
  }

  /**
   * Plain load, no `crossOrigin`: storage answers without CORS headers, and asking for them makes the
   * browser drop the image entirely — which left the canvas at its default 300x150. Nothing here
   * reads the canvas back, so tainting it costs nothing. The rest of the app loads images the same
   * way.
   */
  private loadImage(source: string): Promise<HTMLImageElement> {
    return new Promise((resolve: (element: HTMLImageElement) => void, reject: () => void) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject();
      element.src = source;
    });
  }

  private paintBackground(element: HTMLImageElement): void {
    // The panel can be collapsed or rebuilt while the image loads.
    if (!this.canvas || !this.editorContainer) {
      this.loading = false;
      return;
    }

    const image = new fabric.Image(element);
    this.backgroundWidth = element.naturalWidth || element.width;
    this.backgroundHeight = element.naturalHeight || element.height;

    const available = this.editorContainer.nativeElement.clientWidth || 720;
    this.canvasWidth = Math.min(available, 720);
    this.canvasHeight = this.canvasWidth * (this.backgroundHeight / this.backgroundWidth);

    this.canvas.setWidth(this.canvasWidth);
    this.canvas.setHeight(this.canvasHeight);

    image.set({ selectable: false, evented: false });
    image.scaleToWidth(this.canvasWidth);
    this.canvas.setBackgroundImage(image, () => this.canvas.renderAll());

    this.loading = false;
    this.syncUnlockedRatio();
    this.renderSlots();
  }

  /** A new background can have different dimensions, so the canvas is rebuilt from scratch. */
  private resetCanvas(): void {
    if (this.canvas) {
      this.canvas.dispose();
      this.canvas = null;
    }
    this.slotObjects = [];
    this.labelObjects = [];
    this.cropObjects = [];
    this.photoObjects = [];
    this.canvasWidth = 0;
    this.canvasHeight = 0;
    setTimeout(() => this.drawBackground());
  }

  // -- model <-> canvas ----------------------------------------------------------------------

  get position(): IPrintTemplatePosition {
    return this.positions[ this.activeSlot ];
  }

  /** The ratio being laid out, which is now a tab inside the template rather than the template itself. */
  get aspectRatio(): TemplateAspectRatio {
    return this.activeRatio;
  }

  get isLegacy(): boolean {
    return !this.aspectRatio;
  }

  private renderSlots(): void {
    if (!this.canvas || !this.canvasWidth) {
      return;
    }

    const flatCrops = this.cropObjects.reduce((all: any[], pair: any[]) => all.concat(pair), []);
    for (const object of this.slotObjects.concat(this.labelObjects).concat(flatCrops).concat(this.photoObjects)) {
      this.canvas.remove(object);
    }
    this.slotObjects = [];
    this.labelObjects = [];
    this.cropObjects = [];
    this.photoObjects = [];

    this.positions.forEach((position: IPrintTemplatePosition, index: number) => {
      const rect = new fabric.Rect({
        originX: 'center',
        originY: 'center',
        left: (position.x + position.width / 2) * this.canvasWidth,
        top: (position.y + position.height / 2) * this.canvasHeight,
        width: position.width * this.canvasWidth,
        height: position.height * this.canvasHeight,
        fill: 'rgba(59, 130, 246, 0.22)',
        stroke: '#2563eb',
        strokeWidth: 2,
        strokeUniform: true,
        lockRotation: true,
        transparentCorners: false,
        cornerColor: '#2563eb',
        cornerSize: 8,
      });
      rect.setControlsVisibility(this.controlsVisibility());
      (<any>rect).slotIndex = index;

      const label = new fabric.Text(String(index + 1), {
        originX: 'center',
        originY: 'center',
        fontSize: 16,
        fontFamily: 'sans-serif',
        fill: '#1e3a8a',
        selectable: false,
        evented: false,
      });

      const bands = [ 0, 1 ].map(() => new fabric.Rect({
        originX: 'center',
        originY: 'center',
        fill: 'rgba(217, 83, 79, 0.45)',
        stroke: '#d9534f',
        strokeWidth: 1,
        strokeUniform: true,
        selectable: false,
        evented: false,
        visible: false,
      }));

      const photo = this.testPhotoElement ? new fabric.Image(this.testPhotoElement, {
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
      }) : null;

      this.slotObjects.push(rect);
      this.labelObjects.push(label);
      this.cropObjects.push(bands);
      this.photoObjects.push(photo);

      if (photo) {
        rect.set({ fill: 'transparent' });
        this.canvas.add(photo);
      }
      this.canvas.add(rect);
      bands.forEach((band: any) => this.canvas.add(band));
      this.canvas.add(label);
    });

    this.syncOverlays();
    this.selectActiveObject();
    this.canvas.renderAll();
  }

  /** Model -> canvas, without firing the canvas handlers back into the model. */
  private applyToCanvas(): void {
    if (this.syncing || !this.canvas) {
      return;
    }
    // Editing the main layout carries every combination still tied to it.
    if (this.isPrimaryCombination) {
      this.syncLinkedCombinations();
    }
    this.syncing = true;

    this.positions.forEach((position: IPrintTemplatePosition, index: number) => {
      const rect = this.slotObjects[ index ];
      if (!rect) {
        return;
      }
      rect.set({
        left: (position.x + position.width / 2) * this.canvasWidth,
        top: (position.y + position.height / 2) * this.canvasHeight,
        scaleX: 1,
        scaleY: 1,
        width: position.width * this.canvasWidth,
        height: position.height * this.canvasHeight,
      });
      rect.setCoords();
    });

    this.syncOverlays();
    this.canvas.renderAll();
    this.syncing = false;
  }

  /** Canvas -> model. */
  private captureFromCanvas(): void {
    if (this.syncing) {
      return;
    }
    this.syncing = true;

    this.slotObjects.forEach((rect: any, index: number) => {
      const position = this.positions[ index ];
      if (!position) {
        return;
      }
      const width = rect.getScaledWidth() / this.canvasWidth;
      const height = rect.getScaledHeight() / this.canvasHeight;

      position.width = width;
      position.height = height;
      position.x = rect.left / this.canvasWidth - width / 2;
      position.y = rect.top / this.canvasHeight - height / 2;
    });

    this.syncing = false;
    // Dragging the main layout carries every combination tied to it.
    if (this.isPrimaryCombination) {
      this.syncLinkedCombinations();
    }
    this.syncOverlays();
  }

  private syncOverlays(): void {
    this.slotObjects.forEach((rect: any, index: number) => {
      const label = this.labelObjects[ index ];
      if (label) {
        label.set({ left: rect.left, top: rect.top });
        label.setCoords();
      }
      // Order matters: the bands are measured off the photo this call places.
      this.syncTestPhoto(index, rect);
      this.syncCropBands(index, rect);
    });

    if (this.canvas) {
      this.canvas.requestRenderAll();
    }
  }

  /**
   * Scales the test photo until it covers the slot and clips it to the slot rectangle, which is
   * exactly what the renderer does — so what staff sees here is what prints.
   */
  private syncTestPhoto(index: number, rect: any): void {
    const photo = this.photoObjects[ index ];
    const position = this.positions[ index ];
    if (!photo || !position || !this.testPhotoElement) {
      return;
    }

    const boxWidth = rect.getScaledWidth();
    const boxHeight = rect.getScaledHeight();

    // The renderer lays every photo down on its long edge before it does anything else, and the
    // stored angle is measured from there. An upright example photo is therefore turned flat first,
    // which is why the same angle suits both orientations.
    const natural = { width: this.testPhotoElement.width, height: this.testPhotoElement.height };
    // Turned the other way: laying it on the opposite long edge puts it upside down.
    const laidFlat = natural.height > natural.width ? -90 : 0;
    const angle = this.effectiveRotation(position) + laidFlat;
    const quarterTurn = this.isQuarterTurn(position);

    // What staff uploaded is only a stand-in: the tab says which ratio is being laid out, so the
    // photo is cropped to that first and the result covered into the slot — what the renderer does,
    // and what the red bands measure.
    const photoRatio = aspectRatioValue(this.activeRatio);
    const shownWidth = quarterTurn ? 1 : photoRatio;
    const shownHeight = quarterTurn ? photoRatio : 1;
    const cover = Math.max(boxWidth / shownWidth, boxHeight / shownHeight);
    const frameWidth = shownWidth * cover;
    const frameHeight = shownHeight * cover;

    // Measured after the photo has been laid flat, the way the renderer sees it.
    const flatWidth = laidFlat ? natural.height : natural.width;
    const flatHeight = laidFlat ? natural.width : natural.height;
    const turnedWidth = quarterTurn ? flatHeight : flatWidth;
    const turnedHeight = quarterTurn ? flatWidth : flatHeight;
    const scale = Math.max(frameWidth / turnedWidth, frameHeight / turnedHeight);

    photo.set({
      left: rect.left,
      top: rect.top,
      angle: -angle,
      scaleX: scale,
      scaleY: scale,
      clipPath: new fabric.Rect({
        originX: 'center',
        originY: 'center',
        left: rect.left,
        top: rect.top,
        width: boxWidth,
        height: boxHeight,
        absolutePositioned: true,
      }),
    });
    photo.setCoords();
    this.canvas.sendToBack(photo);
  }

  /**
   * Draws what cover-cropping cuts away: a photo of the ratio being laid out, covered into the slot.
   * It is measured from the ratio rather than from the example photo, so the bands say the same
   * thing whatever staff happened to upload — and match the tab they are looking at.
   */
  private syncCropBands(index: number, rect: any): void {
    const bands = this.cropObjects[ index ];
    const position = this.positions[ index ];
    if (!bands || !position) {
      return;
    }

    const boxWidth = rect.getScaledWidth();
    const boxHeight = rect.getScaledHeight();
    const ratio = this.photoRatioFor(position);

    if (!ratio || !boxWidth || !boxHeight) {
      bands.forEach((band: any) => band.set({ visible: false }));
      return;
    }

    const cover = Math.max(boxWidth / ratio, boxHeight);
    const drawnWidth = cover * ratio;
    const drawnHeight = cover;

    const overflowX = (drawnWidth - boxWidth) / 2;
    const overflowY = (drawnHeight - boxHeight) / 2;
    const horizontal = overflowX > overflowY;
    const overflow = horizontal ? overflowX : overflowY;

    if (overflow <= 0.5) {
      bands.forEach((band: any) => band.set({ visible: false }));
      return;
    }

    bands.forEach((band: any, side: number) => {
      const direction = side === 0 ? -1 : 1;
      band.set({
        visible: true,
        width: horizontal ? overflow : boxWidth,
        height: horizontal ? boxHeight : overflow,
        scaleX: 1,
        scaleY: 1,
        left: rect.left + (horizontal ? direction * (boxWidth + overflow) / 2 : 0),
        top: rect.top + (horizontal ? 0 : direction * (boxHeight + overflow) / 2),
      });
      band.setCoords();
      this.canvas.bringToFront(band);
    });
  }

  get rotationChoice(): number {
    return this.position && this.position.rotation ? this.position.rotation : 0;
  }

  /**
   * Turns the slot a quarter, independently of whether its proportions are locked: the rectangle
   * swaps its sides around its own centre and the photo follows.
   *
   * Stored degrees are anticlockwise, so 90 puts the top of the photo on the left. Fabric measures
   * the other way, hence the negation when drawing.
   */
  setRotation(value: any): void {
    const position = this.position;
    if (!position) {
      return;
    }

    const previous = position.rotation || 0;
    position.rotation = parseInt(value, 10) || 0;

    // The window turns with the photo.
    if ((previous % 180 === 0) !== (position.rotation % 180 === 0)) {
      this.swapSlotSides(position);
    }

    this.applyToCanvas();
  }

  /** Turns the window a quarter around its own centre, so the slot follows the photo. */
  private swapSlotSides(position: IPrintTemplatePosition): void {
    if (!this.backgroundWidth || !this.backgroundHeight) {
      return;
    }
    const centreX = position.x + position.width / 2;
    const centreY = position.y + position.height / 2;
    const width = position.height * this.backgroundHeight / this.backgroundWidth;
    const height = position.width * this.backgroundWidth / this.backgroundHeight;

    position.width = width;
    position.height = height;
    position.x = centreX - width / 2;
    position.y = centreY - height / 2;
  }

  private effectiveRotation(position: IPrintTemplatePosition): number {
    return position.rotation || 0;
  }

  /** Width / height of the test photo as loaded, or 0 when none is in yet. */
  private get loadedPhotoRatio(): number {
    return this.testPhotoElement && this.testPhotoElement.height
      ? this.testPhotoElement.width / this.testPhotoElement.height
      : 0;
  }

  private isQuarterTurn(position: IPrintTemplatePosition): boolean {
    const rotation = this.effectiveRotation(position);
    return rotation === 90 || rotation === 270;
  }

  /** Photo width / height once turned the way this slot prints it. */
  private photoRatioFor(position: IPrintTemplatePosition): number {
    const ratio = aspectRatioValue(this.activeRatio);

    return this.isQuarterTurn(position) ? 1 / ratio : ratio;
  }

  private selectActiveObject(): void {
    const rect = this.slotObjects[ this.activeSlot ];
    if (rect) {
      this.canvas.setActiveObject(rect);
    }
  }

  private selectFromCanvas(event: any): void {
    const target = event.target || (event.selected || [])[ 0 ];
    if (target && typeof target.slotIndex === 'number') {
      this.activeSlot = target.slotIndex;
      this.syncUnlockedRatio();
      this.canvas.renderAll();
    }
  }

  private snap(target: any): void {
    if (!target) {
      return;
    }
    const candidatesX = [ this.canvasWidth / 2 ];
    const candidatesY = [ this.canvasHeight / 2 ];

    this.slotObjects.forEach((rect: any, index: number) => {
      if (index === target.slotIndex) {
        return;
      }
      candidatesX.push(rect.left);
      candidatesY.push(rect.top);
    });

    for (const candidate of candidatesX) {
      if (Math.abs(target.left - candidate) < SNAP_THRESHOLD_PX) {
        target.set({ left: candidate });
      }
    }
    for (const candidate of candidatesY) {
      if (Math.abs(target.top - candidate) < SNAP_THRESHOLD_PX) {
        target.set({ top: candidate });
      }
    }
  }

  // -- numeric fields ------------------------------------------------------------------------

  private get sheet(): { width: number; height: number } {
    return sheetSizeMm(this.backgroundWidth || 3, this.backgroundHeight || 2);
  }

  /** How many display units the full sheet spans along the given axis. */
  private unitSpan(axis: 'x' | 'y'): number {
    if (this.units === 'px') {
      return axis === 'x' ? this.backgroundWidth : this.backgroundHeight;
    }
    const millimetres = axis === 'x' ? this.sheet.width : this.sheet.height;
    return this.units === 'inch' ? millimetres / 25.4 : millimetres;
  }

  /** Step of the numeric inputs, fine enough to matter in whichever unit is on screen. */
  get step(): number {
    if (this.units === 'px') {
      return 1;
    }
    return this.units === 'inch' ? 0.01 : 0.1;
  }

  /** The whole sheet in whichever unit is on screen, so the slot numbers have something to sit against. */
  get sheetSizeLabel(): string {
    const decimals = this.units === 'inch' ? 100 : 10;
    const width = Math.round(this.unitSpan('x') * decimals) / decimals;
    const height = Math.round(this.unitSpan('y') * decimals) / decimals;

    return width + ' x ' + height + ' ' + this.units;
  }

  /** Normalised value -> displayed number, in mm, inches or background pixels. */
  display(value: number, axis: 'x' | 'y'): number {
    if (!value && value !== 0) {
      return 0;
    }
    const decimals = this.units === 'inch' ? 100 : 10;
    return Math.round(value * this.unitSpan(axis) * decimals) / decimals;
  }

  /** Displayed number -> normalised value. Only the display is rounded, never the model. */
  private toNormalised(value: number, axis: 'x' | 'y'): number {
    const span = this.unitSpan(axis);
    return span ? value / span : 0;
  }

  get slotLeft(): number {
    return this.position ? this.display(this.position.x, 'x') : 0;
  }

  get slotTop(): number {
    return this.position ? this.display(this.position.y, 'y') : 0;
  }

  get slotWidth(): number {
    return this.position ? this.display(this.position.width, 'x') : 0;
  }

  get slotHeight(): number {
    return this.position ? this.display(this.position.height, 'y') : 0;
  }

  setSlotLeft(value: any): void {
    if (this.position) {
      this.position.x = this.toNormalised(parseFloat(value) || 0, 'x');
      this.applyToCanvas();
    }
  }

  setSlotTop(value: any): void {
    if (this.position) {
      this.position.y = this.toNormalised(parseFloat(value) || 0, 'y');
      this.applyToCanvas();
    }
  }

  setSlotWidth(value: any): void {
    if (!this.position) {
      return;
    }
    this.position.width = this.toNormalised(parseFloat(value) || 0, 'x');
    this.position.height = this.lockHeight(this.position.width, this.position.height);
    this.applyToCanvas();
  }

  setSlotHeight(value: any): void {
    if (this.position) {
      this.position.height = this.toNormalised(parseFloat(value) || 0, 'y');
      this.applyToCanvas();
    }
  }

  /**
   * Slot height that matches the template's photo ratio in the slot's current orientation. Left
   * alone for a legacy template, which has no ratio to lock to, or once staff unlocks it.
   */
  private lockHeight(width: number, fallback: number): number {
    if (this.unlockedRatio || this.isLegacy || !this.backgroundWidth || !this.backgroundHeight) {
      return fallback;
    }
    const portrait = fallback * this.backgroundHeight >= width * this.backgroundWidth;
    return lockedHeight(width, <PositionAspectRatio>this.aspectRatio,
      this.backgroundWidth, this.backgroundHeight, portrait);
  }

  /** Gap between the active slot and the previous one, along whichever axis they are stacked on. */
  get gapAxis(): 'x' | 'y' {
    const previous = this.positions[ this.activeSlot - 1 ];
    if (!previous || !this.position) {
      return 'y';
    }
    return Math.abs(this.position.x - previous.x) > Math.abs(this.position.y - previous.y) ? 'x' : 'y';
  }

  get gap(): number {
    const previous = this.positions[ this.activeSlot - 1 ];
    if (!previous || !this.position) {
      return 0;
    }
    const value = this.gapAxis === 'x'
      ? this.position.x - (previous.x + previous.width)
      : this.position.y - (previous.y + previous.height);
    return this.display(value, this.gapAxis);
  }

  setGap(value: any): void {
    const previous = this.positions[ this.activeSlot - 1 ];
    if (!previous || !this.position) {
      return;
    }
    const gap = this.toNormalised(parseFloat(value) || 0, this.gapAxis);
    if (this.gapAxis === 'x') {
      this.position.x = previous.x + previous.width + gap;
    } else {
      this.position.y = previous.y + previous.height + gap;
    }
    this.applyToCanvas();
  }

  // -- state ---------------------------------------------------------------------------------

  get crop(): { percent: number; edges: string } {
    if (!this.position) {
      return { percent: 0, edges: '' };
    }
    return cropInfo(this.position, this.aspectRatio, this.backgroundWidth, this.backgroundHeight,
      this.isQuarterTurn(this.position));
  }

  get cropPercent(): number {
    return Math.round(this.crop.percent * 10) / 10;
  }

  get partiallyOutside(): boolean {
    return this.positions.some(partlyOutside);
  }

  get outsideTemplate(): boolean {
    return this.positions.some(fullyOutside);
  }

  /**
   * The save sends every combination, not the one on screen, so the check has to look at all of
   * them. Otherwise a slot dragged off the sheet in one tab is saved as soon as another tab is
   * selected. Returns the combinations that fail, labelled the way the tabs are.
   */
  private failing(test: (position: IPrintTemplatePosition) => boolean): string[] {
    return this.combinations
      .filter(({ ratio, orientation }: any) =>
        ((this.layouts[ ratio ] || <any>{})[ orientation ] || []).some(test))
      .map(({ ratio, orientation }: any) => ratio + ' ' + orientation);
  }

  /** Switches which set of slots is being edited, and the photo drawn into them. */
  selectOrientation(orientation: PhotoOrientation): void {
    if (orientation === this.activeOrientation) {
      return;
    }
    this.activeOrientation = orientation;
    this.activeSlot = 0;
    this.syncUnlockedRatio();

    // Follow the switch with the matching example photo, when one has been loaded.
    const match = this.testPhotos.findIndex(
      (photo: { label: string }) => photo.label.toLowerCase() === orientation
    );
    if (match >= 0 && match !== this.activeTestPhoto) {
      this.selectTestPhoto(match);
      return;
    }

    this.renderSlots();
  }

  selectSlot(index: number): void {
    this.activeSlot = index;
    this.syncUnlockedRatio();
    this.selectActiveObject();
    this.canvas.renderAll();
  }

  /**
   * A slot that already ignores the photo ratio opens with that box ticked, so editing it does not
   * silently snap the window back to the photo's shape. Every switch of slot, orientation or ratio
   * brings up a different slot, so the box has to be read off that one rather than the last.
   */
  private syncUnlockedRatio(): void {
    this.unlockedRatio = this.cropPercent > 0.1;
    this.applyControlsVisibility();
  }

  /**
   * Corners always scale the slot; the side handles would break the ratio, so they only show once
   * proportions are explicitly unlocked.
   */
  private controlsVisibility(): any {
    return {
      mtr: false,
      ml: this.unlockedRatio,
      mr: this.unlockedRatio,
      mt: this.unlockedRatio,
      mb: this.unlockedRatio,
      tl: true,
      tr: true,
      bl: true,
      br: true,
    };
  }

  /**
   * The handles are built once per render, so a slot selected afterwards would keep the previous
   * slot's set — side handles on a locked slot let it be dragged out of the photo's shape.
   */
  private applyControlsVisibility(): void {
    const visibility = this.controlsVisibility();
    this.slotObjects.forEach((rect: any) => rect.setControlsVisibility(visibility));
  }

  toggleUnlockedRatio(): void {
    this.unlockedRatio = !this.unlockedRatio;
    if (!this.unlockedRatio && this.position) {
      this.position.height = this.lockHeight(this.position.width, this.position.height);
    }
    this.renderSlots();
  }

  /** Throws local edits away and takes the positions the API currently holds. */
  discardChanges(): void {
    if (this.template) {
      this.load();
    }
  }

  @HostListener('document:keydown', [ '$event' ])
  onKeydown(event: KeyboardEvent): void {
    const keys: { [key: string]: [ 'x' | 'y', number ] } = {
      ArrowLeft: [ 'x', -1 ],
      ArrowRight: [ 'x', 1 ],
      ArrowUp: [ 'y', -1 ],
      ArrowDown: [ 'y', 1 ],
    };
    const move = keys[ event.key ];
    if (!move || !this.position || !this.isEditMode) {
      return;
    }
    const target = <HTMLElement>event.target;
    if (target && /input|select|textarea/i.test(target.tagName)) {
      return;
    }
    event.preventDefault();

    const [ axis, direction ] = move;
    const millimetres = (event.shiftKey ? NUDGE_MM_LARGE : NUDGE_MM) * direction;
    const factor = axis === 'x' ? this.sheet.width : this.sheet.height;
    this.position[ axis ] += millimetres / factor;
    this.applyToCanvas();
  }

  // -- test photo ----------------------------------------------------------------------------

  /**
   * Test photos are stored on the template, one per orientation, so the slots are never drawn empty
   * after staff has uploaded them once.
   */
  private loadTestPhoto(): void {
    this.buildDraftPhotos();
  }

  /** Measures the photo before offering it, so the switcher can say which way round it is. */
  private addTestPhoto(source: string, select: boolean = false): void {
    const element = new Image();
    element.onload = () => {
      const label = element.width >= element.height ? 'Horizontal' : 'Vertical';
      this.testPhotos.push({ url: source, label: label });
      if (select || this.activeTestPhoto < 0) {
        this.selectTestPhoto(this.testPhotos.length - 1);
      }
    };
    element.src = source;
  }

  selectTestPhoto(index: number): void {
    const photo = this.testPhotos[ index ];
    if (!photo) {
      return;
    }
    this.activeTestPhoto = index;

    const element = new Image();
    element.onload = () => {
      this.testPhotoElement = element;
      // Show the set the photo actually belongs to, so the preview is never of the wrong layout.
      this.activeOrientation = element.width >= element.height ? 'horizontal' : 'vertical';
      this.activeSlot = 0;
      this.syncUnlockedRatio();
      this.renderSlots();
    };
    element.src = photo.url;
  }

  get testPhotoUrl(): string {
    const photo = this.testPhotos[ this.activeTestPhoto ];
    return photo ? photo.url : null;
  }

  /**
   * Test photos have an upload rule of their own per orientation: the ratio string cannot tell
   * 1200x1800 from 1800x1200, so the orientation lives in the rule name and a portrait photo
   * uploaded as horizontal is rejected on resolution.
   */

  /** The photo is stored on the template, so the editor draws the same one on the next visit. */
  /** Drops the photo from the preview only — the API has no way to take one off a template. */
  clearTestPhoto(): void {
    this.testPhotoElement = null;
    this.activeTestPhoto = -1;
    this.renderSlots();
  }

  // -- background ----------------------------------------------------------------------------

  /**
   * Config for the shared indirect upload zone. Same three-step flow as the CMS tab, but against the
   * admin endpoint: the admin panel is cross-campaign and holds no campaign session, so it cannot
   * use the campaign-scoped CMS upload.
   */
  get uploadConfig(): any {
    return {
      url: this.uploadUrl,
      fileType: 'image',
      relatedTo: 'keyring',
      // The artwork is a 6x4 sheet whatever the template is for, and the rule checks the real shape
      // of the image, so the template's own ratio must not be sent here.
      aspectRatio: '3x2',
      onFileUploaded: this.onBackgroundUploaded.bind(this),
    };
  }

  /** The admin upload pair takes the campaign uuid in the path, not its id. */
  private get uploadUrl(): string {
    const campaign = this.template ? this.template.campaign : null;
    return '/admin/campaign/' + (campaign && campaign.uuid ? campaign.uuid : '') + '/file/upload';
  }

  /**
   * The shared uploader hands back the stored file; the template then points at it. Reusing that
   * component keeps admin on exactly the same upload path as the CMS tab.
   */
  onBackgroundUploaded(file: any): void {
    if (!file || !file.id) {
      return;
    }

    // A draft has no row on the API to attach the file to — it carries it into the create instead.
    if (this.isDraft) {
      this.template.file = file;
      this.row.file = file;
      this.resetCanvas();
      return;
    }

    this.httpClient
      .put<any>(this.urlBuilder.buildUrl('/print-template') + '/' + this.row.id, { file: +file.id })
      .subscribe(
        (template: IPrintTemplate) => {
          this.template = template && template.file ? template : { ...this.template, file: file };
          this.row.file = this.template.file;
          this.resetCanvas();
          this.toastrService.success('Background replaced');
        },
        (error: any) => this.toastrService.error(this.errorMessage(error))
      );
  }

  // -- persistence ---------------------------------------------------------------------------

  save(): void {
    if (!this.templateName) {
      this.toastrService.error('Enter a name');
      return;
    }
    if (!this.usesDefaultTemplate && !this.isDraft && !this.template.file) {
      this.toastrService.error('Upload artwork first, or keep the GoPhoto design');
      return;
    }
    const outside = this.failing(fullyOutside);
    if (outside.length) {
      this.toastrService.error('A slot is completely outside the template (' + outside.join(', ') + ')');
      return;
    }
    const sticking = this.failing(partlyOutside);
    if (sticking.length) {
      this.toastrService.warning(
        'A slot sticks out of the template (' + sticking.join(', ') + ') — saving anyway');
    }

    // Everything goes every time — every ratio, both orientations. A combination tied to the main
    // one goes as `null` rather than a copy of it: the tie is then in the data, and changing the
    // main layout later carries without anyone comparing coordinates.
    //
    // A legacy template has no main layout to tie anything to, and inventing empty slot sets for it
    // would turn it into a positioned template that prints nothing. It stays empty instead — but
    // only while it is still legacy: once a layout is stored, an empty main set is the stored answer
    // and collapsing it back to `{}` would drop the other combinations with it.
    const positions: IPrintTemplateLayouts = {};
    if (this.hasStoredLayout || this.layouts[ PRIMARY_LAYOUT_RATIO ][ PRIMARY_LAYOUT_ORIENTATION ].length) {
      this.combinations.forEach(({ ratio, orientation }: any) => {
        positions[ ratio ] = positions[ ratio ] || <IPrintTemplatePositions>{ horizontal: null, vertical: null };
        positions[ ratio ][ orientation ] = this.isLinkedCombination(ratio, orientation)
          ? null
          : this.layouts[ ratio ][ orientation ].map((position: IPrintTemplatePosition) => ({ ...position }));
      });
    }

    if (this.isDraft) {
      this.createDraft(positions);
      return;
    }

    this.saving = true;
    this.httpClient
      .put(this.urlBuilder.buildUrl('/print-template') + '/' + this.template.id, {
        name: this.templateName,
        defaultTemplate: this.usesDefaultTemplate,
        positions: positions,
      })
      .subscribe(
        (response: any) => {
          this.saving = false;
          this.usingDefaults = false;
          this.savedSnapshot = this.snapshot();
          if (response && response.positions) {
            this.row.positions = response.positions;
          }
          this.toastrService.success('Positions saved');
        },
        (error: any) => {
          this.saving = false;
          this.toastrService.error(this.errorMessage(error));
        }
      );
  }

  /**
   * The first save is where a draft reaches the API: the artwork and both example photos go up, then
   * the template itself, carrying the layout staff has been dragging around. Nothing partial is left
   * behind if they close the panel instead.
   */
  private createDraft(positions: IPrintTemplateLayouts): void {
    this.saving = true;
    // Nothing is uploaded for a template on the GoPhoto design: the renderer picks the artwork from
    // the type, and the API takes the create without a file.
    const artwork = this.usesDefaultTemplate
      ? Promise.resolve({ artwork: null })
      : this.uploadDraftFiles();

    artwork
      .then((files: any) => this.httpClient
        .post<any>(this.urlBuilder.buildUrl('/print-template'), {
          campaign: this.row.campaign.id,
          name: this.templateName,
          type: this.row.type,
          defaultTemplate: this.usesDefaultTemplate,
          ...(files.artwork ? { file: files.artwork.id } : {}),
          positions: positions,
        })
        .toPromise())
      .then(
        (template: any) => {
          this.saving = false;
          this.toastrService.success('Template created');
          this.created.emit(template);
        },
        (error: any) => {
          this.saving = false;
          this.toastrService.error(this.errorMessage(error));
        }
      );
  }

  /**
   * Only the artwork reaches the API — the example photos are drawn in the browser purely to show
   * what prints, so there is nothing to upload for them. A graphic staff picked is already on
   * storage by now, so all that is left is the shipped design for a draft nobody uploaded one for.
   */
  private async uploadDraftFiles(): Promise<any> {
    const picked = this.template.file && this.template.file.id ? this.template.file : null;
    const artwork = picked || await this.assetUploader.uploadArtwork(this.row.campaign.uuid, this.row.type);

    return { artwork: artwork };
  }

  discardDraft(): void {
    this.discarded.emit();
  }

  /**
   * Renders the combination on screen through the builder the printer uses, and opens it. The tab
   * decides: without it the preview would show whichever photo the campaign happens to have, which
   * says nothing about the other five layouts.
   */
  previewPrint(): void {
    this.previewing = true;
    this.httpClient
      .post<any>(this.urlBuilder.buildUrl('/print-template') + '/' + this.template.id + '/preview', {
        aspectRatio: this.activeRatio,
        orientation: this.activeOrientation,
      })
      .subscribe(
        (response: any) => {
          this.previewing = false;
          if (response && response.url) {
            window.open(response.url, '_blank');
            return;
          }
          this.toastrService.error('The preview came back without a link');
        },
        (error: any) => {
          this.previewing = false;
          this.toastrService.error(this.errorMessage(error));
        }
      );
  }

  private errorMessage(error: any): string {
    return error && error.error ? error.error.message || 'Something went wrong...' : 'Error';
  }
}
